"use client"

import * as React from "react"
import { BarChart3, Home, KeyRound, QrCode } from "lucide-react"

import { useAuth } from '@/auth/context/AuthContext'
import { useTeam } from '@/shared/context/TeamContext'
import { useClientesNav } from '@/modules/clientes'
import { useProgrammaticNav } from '@/modules/programmatic'
import { useAnalyticsNav } from '@/modules/analytics'
import { useWebinarsNav } from '@/modules/webinars'
import { useCemcEducacionNav } from '@/modules/cemc-educacion'
import {
  teams as legacyTeams,
  navMainTonal,
  navMainAdmin,
} from "@/config"
import type { Team } from "@/config"
import { NavMain } from "./nav-main"
import { NavProjects } from "./nav-projects"
import { NavUser } from "./nav-user"
import { TeamSwitcher } from "./team-switcher"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from '@/shared/components/ui/sidebar'

// Items de navegación únicos (sin subitems)
const navSingleItemsBase = [
  {
    title: "Inicio",
    url: "/",
    icon: Home,
  },
  {
    title: "Código QR",
    url: "/codigos-qr",
    icon: QrCode,
    onlyTonal: true,
  },
  {
    title: "Contraseñas",
    url: "/passwords",
    icon: KeyRound,
    onlyTonal: true, // Solo visible en Tonal
  },
  {
    title: "Videos GrupoFarma",
    url: "/dash/grupofarma/webinars",
    icon: BarChart3,
    requiredGroups: ['Grupofarma', 'admin'],
  },
]

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user } = useAuth()
  const { selectedTeam, userAreas, setUserAreas, selectedArea } = useTeam()

  const { grupos: clientesGrupos } = useClientesNav()
  const { grupos: programmaticGrupos } = useProgrammaticNav()
  const { grupos: analyticsGrupos } = useAnalyticsNav()
  const { grupos: webinarsGrupos } = useWebinarsNav()
  const { grupos: cemcEducacionGrupos } = useCemcEducacionNav()

  // Sync areas from auth user into TeamContext
  React.useEffect(() => {
    if (user?.areas) {
      setUserAreas(user.areas)
    }
  }, [user?.areas, setUserAreas])

  // Build dynamic teams from user areas (fallback to legacy if no areas)
  const dynamicTeams: Team[] = React.useMemo(() => {
    if (userAreas.length > 0) {
      // "Admin" va primero y "Tonal" segundo; el resto conserva su orden original.
      const prioridad = (nombre: string) => {
        if (nombre === 'Admin') return 0
        if (nombre === 'Tonal') return 1
        return 2
      }
      const ordenadas = [...userAreas]
        .filter((area) => area.nombre !== 'Tonal Admin')
        .sort((a, b) => prioridad(a.nombre) - prioridad(b.nombre))
      return ordenadas.map((area) => ({
        name: area.nombre,
        logo: area.imagen_url || '/logo_tonal.png',
        plan: area.descripcion || area.nombre,
      }))
    }
    return legacyTeams
  }, [userAreas])

  // Check if user is admin (has access to everything)
  const isTonalAdmin = user?.groups?.includes('admin') || user?.is_superuser

  // Get the groups for the currently selected area
  const areaGroups = React.useMemo(() => {
    if (!selectedArea) return []
    return selectedArea.grupos || []
  }, [selectedArea])

  // Helper: check if a group is available in the current area context
  const areaHasGroup = React.useCallback((groupName: string): boolean => {
    return areaGroups.includes(groupName)
  }, [areaGroups])

  // Determine if the selected area is a special area (Admin) or a regular client area
  const isAdminArea = selectedTeam === "Admin"
  const isTonalArea = selectedTeam === "Tonal"
  const isClientArea = !isAdminArea && !isTonalArea

  // Permission checks scoped to the selected area's groups
  const canSeeCronograma = isTonalAdmin
    ? (isTonalArea || areaHasGroup('mailing') || areaHasGroup('mailing_admin'))
    : (areaHasGroup('mailing') || areaHasGroup('mailing_admin'))

  const canCreateCampania = isTonalAdmin
    ? (isTonalArea || areaHasGroup('mailing_admin'))
    : areaHasGroup('mailing_admin')

  const canSeeWhatsApp = isTonalAdmin && (isTonalArea || isAdminArea) || areaHasGroup('whatsapp')
  const canSeeLinkTracker = isTonalAdmin || areaHasGroup('link_tracker')
  const canSeeRegistros = (isTonalArea || isAdminArea)
    ? (areaHasGroup('Registros') || isTonalAdmin)
    : areaHasGroup('Registros')

  const canSeeBDClientes = isAdminArea
    ? (areaHasGroup('Basedatos') || areaHasGroup('basedatos_clientes') || isTonalAdmin)
    : false

  const canSeeSorteos = isTonalAdmin || areaHasGroup('sorteos') || areaHasGroup('sorteos_admin')
  const canCreateSorteo = isTonalAdmin || areaHasGroup('sorteos_admin')

  const canSeeQRCodigo = isAdminArea
    ? (areaHasGroup('QR_codigo_unico') || isTonalAdmin)
    : false

  const canSeeConsumo = selectedTeam === 'WTonal' || (isAdminArea && isTonalAdmin)

  // BD Unificada (microservicio externo) — solo en el área Admin
  const canSeeBdUnificada = isAdminArea
    ? (areaHasGroup('BdUnificada') || areaHasGroup('BdUnificada-admin') || isTonalAdmin)
    : false
  const canAdminBdUnificada = isAdminArea
    ? (areaHasGroup('BdUnificada-admin') || isTonalAdmin)
    : false

  // Map area/team name to navigation configuration
  const baseNavItems = React.useMemo(() => {
    if (isAdminArea) return navMainAdmin
    // Tonal and all client areas use navMainTonal as base (filtered by area groups)
    return navMainTonal
  }, [isAdminArea])

  // Check if user can see Contraseñas
  const canSeeContrasenas = (isTonalArea || isAdminArea) && (user?.groups?.includes('contrasenas') || isTonalAdmin)

  // Filter single items based on selected team and permissions
  const navSingleItems = navSingleItemsBase.filter(item => {
    if ('requiredGroups' in item && item.requiredGroups) {
      return isTonalAdmin || item.requiredGroups.some(g => user?.groups?.includes(g))
    }
    if (item.onlyTonal) {
      if (item.url === "/passwords") {
        if (isClientArea || isAdminArea) return false
        return canSeeContrasenas
      }
      if (item.url === "/codigos-qr") {
        return isTonalAdmin || isTonalArea
      }
      if (isClientArea || isAdminArea) return false
    }
    return true
  })

  // Groups configuration for each dynamic section
  const sectionGroups: Record<string, { admin: string; base: string }> = {
    "CEMC": { admin: "CEMC-admin", base: "CEMC" },
    "CEMC Reportes": { admin: "CEMC-Educacion-admin", base: "CEMC-Educacion" },
    "Programmatic": { admin: "Programmatic-admin", base: "Programmatic" },
    "Analytics": { admin: "Analytics-admin", base: "Analytics" },
    "Webinars": { admin: "Webinars-admin", base: "Webinars" },
  }

  // Check if user can see a dynamic section (scoped to the area's groups)
  const canSeeSection = (sectionTitle: string): boolean => {
    const config = sectionGroups[sectionTitle]
    if (!config) return true
    // For Tonal area or admin, show if user globally has the group
    if (isTonalArea || isAdminArea) {
      if (isTonalAdmin) return true
      return !!(user?.groups?.includes(config.base) || user?.groups?.includes(config.admin))
    }
    // For client areas, show only if the area has the group
    return areaHasGroup(config.base) || areaHasGroup(config.admin)
  }

  // Helper to inject dynamic labs into a nav section
  const injectDynamicLabs = (
    item: typeof baseNavItems[0],
    title: string,
    grupos: { id: number; name: string; slug: string }[],
    basePath: string,
  ) => {
    if (item.title !== title) return null
    const config = sectionGroups[title]
    const canConfigure = isTonalAdmin || (config && user?.groups?.includes(config.admin))
    return {
      ...item,
      items: [
        ...grupos.map(g => ({ title: g.name, url: `/${basePath}/${g.slug}` })),
        ...(canConfigure ? [{ title: "Configuración", url: `/${basePath}/configuracion` }] : []),
      ],
    }
  }

  // Inyección específica de CEMC Reportes (cemc-educacion): los sub-items apuntan
  // al reporte por id de configuración, no por slug de grupo.
  const injectCemcEducacion = (item: typeof baseNavItems[0]) => {
    if (item.title !== "CEMC Reportes") return null
    const config = sectionGroups["CEMC Reportes"]
    const canConfigure = isTonalAdmin || (config && user?.groups?.includes(config.admin))
    return {
      ...item,
      items: [
        ...cemcEducacionGrupos.map(g => ({ title: g.name, url: `/cemc-educacion/reporte/${g.id}` })),
        ...(canConfigure ? [{ title: "Configuración", url: `/cemc-educacion/configuracion` }] : []),
      ],
    }
  }

  // Filter navigation items based on user permissions
  const navItems = baseNavItems
    .filter(item => {
      // Multichannel is only visible for users in mailing or mailing_admin groups
      // In Admin area, Multichannel section is for seguimiento-reportes (admin only)
      if (item.title === "Multichannel") {
        if (isAdminArea) return isTonalAdmin
        return canSeeCronograma
      }
      // WhatsApp is only visible for users in Tonal group
      if (item.title === "WhatsApp") {
        return canSeeWhatsApp
      }
      // BD Clientes is only visible for Basedatos, basedatos_clientes or admin
      if (item.title === "BD Clientes") {
        return canSeeBDClientes
      }
      // Registros is only visible for Registros or admin
      if (item.title === "Registros") {
        return canSeeRegistros
      }
      // Link Tracker is only visible for admin
      if (item.title === "Link Tracker") {
        return canSeeLinkTracker
      }
      // Sorteos is only visible for sorteos group or admin
      if (item.title === "Sorteos") {
        return canSeeSorteos
      }
      // QR Código is only visible for QR_codigo_unico or admin in Admin area
      if (item.title === "QR Código") {
        return canSeeQRCodigo
      }
      // Usuarios is only visible for admin in Admin area
      if (item.title === "Usuarios") {
        return isTonalAdmin && isAdminArea
      }
      // BD Unificada is only visible in Admin area for BdUnificada groups or admin
      if (item.title === "BD Unificada") {
        return canSeeBdUnificada
      }
      // Consumo IA y Consumo Meta solo visibles para el área WTonal (o admin en Admin)
      if (item.title === "Consumo IA") {
        return canSeeConsumo
      }
      if (item.title === "Consumo Meta") {
        return canSeeConsumo
      }
      // Dynamic sections: only visible if user belongs to that section's groups
      const dynamicSections = ["CEMC", "CEMC Reportes", "Programmatic", "Analytics", "Webinars"]
      if (dynamicSections.includes(item.title)) {
        return canSeeSection(item.title)
      }
      // Items with groups property: check against area groups for client areas
      if (item.groups && item.groups.length > 0) {
        if (isTonalArea || isAdminArea) {
          if (isTonalAdmin) return true
          return item.groups.some(g => user?.groups?.includes(g))
        }
        // Client area: only show if the area has one of the required groups
        return item.groups.some(g => areaHasGroup(g))
      }
      return true
    })
    .map(item => {
      // Filter sub-items for Registros - Configuracion only visible for admin
      if (item.title === "Registros" && item.items) {
        return {
          ...item,
          items: item.items.filter(subItem => {
            if (subItem.title === "Configuracion") {
              return isTonalAdmin
            }
            return true
          })
        }
      }
      // Filter sub-items for Multichannel - Nueva Campana only visible for mailing_admin or admin
      if (item.title === "Multichannel" && item.items) {
        return {
          ...item,
          items: item.items.filter(subItem => {
            if (subItem.title === "Nueva Campana" || subItem.title === "Nueva Campaña") {
              return canCreateCampania
            }
            return true
          })
        }
      }
      // Filter sub-items for Fidelización - Configuración only visible for admin or fidelizacion_admin
      if (item.title === "Fidelización" && item.items) {
        return {
          ...item,
          items: item.items.filter(subItem => {
            if (subItem.title === "Configuración") {
              return isTonalAdmin || user?.groups?.includes('fidelizacion_admin')
            }
            return true
          })
        }
      }

      // Filter sub-items for BD Unificada - solo Contactos para viewers; resto solo admin
      if (item.title === "BD Unificada" && item.items) {
        return {
          ...item,
          items: item.items.filter(subItem => {
            if (subItem.title === "Contactos") return true
            return canAdminBdUnificada
          })
        }
      }
      // Filter sub-items for Sorteos - Crear Sorteo only visible for sorteos_admin or admin
      if (item.title === "Sorteos" && item.items) {
        return {
          ...item,
          items: item.items.filter(subItem => {
            if (subItem.title === "Crear Sorteo") {
              return canCreateSorteo
            }
            return true
          })
        }
      }

      // Inject dynamic lab subitems for CEMC
      const cemc = injectDynamicLabs(item, "CEMC", clientesGrupos, "cemc")
      if (cemc) return cemc

      // Inject dynamic report subitems for CEMC Reportes (cemc-educacion)
      const cemcEdu = injectCemcEducacion(item)
      if (cemcEdu) return cemcEdu

      // Inject dynamic lab subitems for Programmatic
      const prog = injectDynamicLabs(item, "Programmatic", programmaticGrupos, "programmatic")
      if (prog) return prog

      // Inject dynamic lab subitems for Analytics
      const anal = injectDynamicLabs(item, "Analytics", analyticsGrupos, "analytics")
      if (anal) return anal

      // Inject dynamic lab subitems for Webinars
      const webi = injectDynamicLabs(item, "Webinars", webinarsGrupos, "webinars")
      if (webi) return webi

      return item
    })
    // Hide dynamic sections if no subitems (no labs and not admin)
    .filter(item => {
      const dynamicSections = ["CEMC", "CEMC Reportes", "Programmatic", "Analytics", "Webinars"]
      if (dynamicSections.includes(item.title)) {
        return item.items && item.items.length > 0
      }
      return true
    })

  // Build user data for NavUser
  const userData = user ? {
    name: `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email,
    email: user.email,
    avatar: user.avatar_url || undefined,
  } : {
    name: "Usuario",
    email: "",
    avatar: undefined,
  }

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <TeamSwitcher teams={dynamicTeams} />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={navItems} singleItems={navSingleItems} />
        <NavProjects />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={userData} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
